import { useMemo, useState } from "react";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import {
  AutoAwesome as SparkleIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";
import { green, grey, red } from "@mui/material/colors";
import { isDev } from "src/helpers/env";
import { snakeToTitleCase } from "src/reusable/Functions";
import {
  deriveCompletionMessage,
  formatToolOutput,
  isToolErrorResult,
  unwrapToolResult,
} from "./toolStatusUtils";
import { useToolRunElapsed } from "./useToolRunElapsed";

interface CompactToolBlockProps {
  toolName: string;
  isLoading: boolean;
  /** Custom status message from the model (e.g., "Searching for safety procedures...") */
  statusMessage?: string;
  /** Full tool call arguments */
  toolArguments?: Record<string, any>;
  /** Parsed tool_response payload; undefined until the response arrives */
  responseContent?: any;
  /** The response renders its own rich UI below — omit result summary/output */
  hideResult?: boolean;
  /**
   * Stable identity of this tool run (`${messageId}-${partIndex}`) so the
   * running timer survives unmount/remount (e.g. Equipment AI's tab rail).
   */
  runKey?: string;
}

/** Compact one-line preview of tool arguments, e.g. `query: "pump seal", limit: 5` */
function buildArgPreview(args: Record<string, any>): string {
  const entries = Object.entries(args).filter(
    ([key]) => key !== "status_message",
  );
  if (!entries.length) return "";
  const joined = entries
    .map(([key, value]) => {
      let rendered: string;
      try {
        rendered =
          typeof value === "string"
            ? `"${value}"`
            : (JSON.stringify(value) ?? String(value));
      } catch {
        rendered = String(value);
      }
      if (rendered.length > 40) rendered = `${rendered.slice(0, 37)}…`;
      return `${key}: ${rendered}`;
    })
    .join(", ");
  return joined.length > 90 ? `${joined.slice(0, 87)}…` : joined;
}

const truncateLine = (line: string, max: number) =>
  line.length > max ? `${line.slice(0, max - 3)}…` : line;

/**
 * Fixed header labels for tools whose results render their own interactive
 * card below the row — the model-written status message tends to restate the
 * card's content there.
 */
const INTERACTIVE_TOOL_LABELS: Record<string, string> = {
  prompt_user_file_upload: "Requesting a file upload",
  prompt_user_selection: "Asking for a selection",
};

/**
 * CompactToolBlock renders a tool call and its result as a Claude-Code-style
 * two-line entry: a header line (`● Tool Name (args…)`) and a dim result
 * summary line (`⎿ …`). In dev, clicking either line expands the full
 * arguments and raw output, and a running tool shows its arguments panel
 * open; in production the rows are static and only curated summary text is
 * shown. Used when `ChatContext.compactToolCalls` is set; card-style chats
 * keep ArtifactCard.
 */
const CompactToolBlock = ({
  toolName,
  isLoading,
  statusMessage,
  toolArguments,
  responseContent,
  hideResult = false,
  runKey,
}: CompactToolBlockProps) => {
  const [expanded, setExpanded] = useState(false);
  const elapsed = useToolRunElapsed(runKey, isLoading);

  const result = useMemo(
    () => unwrapToolResult(responseContent),
    [responseContent],
  );
  const hasError = useMemo(
    () => isToolErrorResult(responseContent) || isToolErrorResult(result),
    [responseContent, result],
  );
  const formattedOutput = useMemo(
    () => (hideResult ? "" : formatToolOutput(result)),
    [hideResult, result],
  );

  // Dim one-line result summary shown while collapsed. Curated text
  // (backend status_summary / derived heuristics) shows everywhere; the
  // fallbacks below the single isDev check expose raw tool internals
  // (error text, raw output) and are dev-only.
  const summary = useMemo(() => {
    if (isLoading || hideResult || responseContent == null) return "";
    const derived = deriveCompletionMessage(responseContent);
    if (derived) return truncateLine(derived, 80);
    if (!isDev) return "";
    if (hasError) {
      const errMsg = typeof result?.error === "string" ? result.error : "Error";
      return truncateLine(errMsg, 80);
    }
    const firstLine =
      formattedOutput.split("\n").find((line) => line.trim()) ?? "";
    return firstLine ? truncateLine(firstLine.trim(), 80) : "Done";
  }, [
    isLoading,
    hideResult,
    responseContent,
    hasError,
    result,
    formattedOutput,
  ]);

  const { status_message: _sm, ...displayArgs } = toolArguments ?? {};
  const argPreview = useMemo(
    () => (hideResult ? "" : buildArgPreview(toolArguments ?? {})),
    [hideResult, toolArguments],
  );
  const label = hideResult
    ? (INTERACTIVE_TOOL_LABELS[toolName] ?? snakeToTitleCase(toolName))
    : statusMessage || snakeToTitleCase(toolName);
  const toggle = () => {
    if (isDev) setExpanded((prev) => !prev);
  };
  // Dev only: show the full call (arguments panel) while the tool is
  // executing; it rolls into the compact one-liner when the result arrives
  // (and from there into the tool-run group). User expansion still works
  // after. In production there is no details panel to expand.
  const effectiveExpanded = isDev && (isLoading || expanded);
  // Static row in production — no button semantics without an interaction
  const RowComponent = (isDev ? ButtonBase : Box) as typeof ButtonBase;

  return (
    <Box data-testid={`aa-tool-artifact-${toolName}`} sx={{ mb: 0.5 }}>
      {/* Header line: ● Tool Name (args…) */}
      <RowComponent
        onClick={isDev ? toggle : undefined}
        aria-expanded={isDev ? effectiveExpanded : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          width: "100%",
          gap: 0.75,
          px: 0.5,
          py: 0.25,
          borderRadius: "4px",
          userSelect: "text",
          ...(isDev && {
            "&:hover": { bgcolor: grey[100] },
            transition: "background-color 0.15s ease",
          }),
        }}
      >
        {isLoading ? (
          <SparkleIcon
            sx={{
              fontSize: 14,
              flexShrink: 0,
              color: "#009688",
              animation: "sparkle 1.5s ease-in-out infinite",
              "@keyframes sparkle": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.5, transform: "scale(0.85)" },
              },
            }}
          />
        ) : (
          <DotIcon
            sx={{
              fontSize: 10,
              flexShrink: 0,
              mx: "2px",
              color: hasError ? red[500] : green[500],
            }}
          />
        )}
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: grey[800],
            fontWeight: 500,
            fontSize: "0.8rem",
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        {isDev && argPreview && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: grey[500],
              fontFamily: "monospace",
              fontSize: "0.72rem",
              minWidth: 0,
            }}
          >
            ({argPreview})
          </Typography>
        )}
        {isLoading && elapsed > 0 && (
          <Typography
            variant="caption"
            sx={{ color: grey[400], fontSize: "0.72rem", flexShrink: 0 }}
          >
            {elapsed}s
          </Typography>
        )}
        {isDev && (
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              color: grey[400],
            }}
          >
            {effectiveExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 14 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        )}
      </RowComponent>

      {/* Collapsed result summary: ⎿ … */}
      {!effectiveExpanded && summary && (
        <RowComponent
          onClick={isDev ? toggle : undefined}
          aria-expanded={isDev ? expanded : undefined}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            textAlign: "left",
            width: "100%",
            pl: 2.75,
            pr: 0.5,
            borderRadius: "4px",
            userSelect: "text",
          }}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: hasError ? red[600] : grey[500],
              fontSize: "0.75rem",
            }}
          >
            ⎿&nbsp;&nbsp;{summary}
          </Typography>
        </RowComponent>
      )}

      {/* Dev only — expanded details: full arguments + raw output */}
      {isDev && (
        <Collapse in={effectiveExpanded}>
          <Box
            sx={{
              ml: "10px",
              mt: 0.25,
              pl: 1.5,
              py: 0.5,
              borderLeft: `2px solid ${grey[200]}`,
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: grey[500],
                fontSize: "0.7rem",
                fontFamily: "monospace",
                mb: 0.5,
              }}
            >
              {toolName}
            </Typography>
            {Object.keys(displayArgs).length > 0 && (
              <Box
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.4,
                  color: grey[600],
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  m: 0,
                  mb: 1,
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                {JSON.stringify(displayArgs, null, 2)}
              </Box>
            )}
            {!hideResult && responseContent != null && (
              <Box
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.72rem",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  m: 0,
                  maxHeight: 300,
                  overflow: "auto",
                  color: hasError ? red[700] : grey[700],
                }}
              >
                {formattedOutput || "No output"}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default CompactToolBlock;
